import { kcdDatadogLogs, kcdDatadogRum } from '@kcd/app-utils/dist/browser';

import { OAIApiError } from '~/libs/openApiError';

import { ENV } from './env';
import { isClient } from './ssr';

/**
 * 설치 가이드 : https://koreacreditdata.atlassian.net/wiki/spaces/ENG/pages/3472786814/Real-User+Monitoring
 * 공식문서 Rum : https://docs.datadoghq.com/ko/real_user_monitoring/browser/
 * 공식문서 Browser logs: https://docs.datadoghq.com/logs/log_collection/javascript/
 * */

export const datadogInitialize = () => {
  if (!ENV) return;
  kcdDatadogRum.init({
    applicationId: '6ea379ae-a03c-49a3-a81a-b7c4e5056388',
    clientToken: 'pube5059ad1fc1207c4765db5316d942fc6',
    env: ENV,
    version: process.env.NEXT_PUBLIC_APP_VERSION, // 배포시 생성되는 release version을 datadog에 똑같이 전달함.
    service: 'market-buyer-front',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    /**
     * 추적 헤더를 삽입하는 데 사용되는 요청 URL 목록
     * https://docs.datadoghq.com/ko/real_user_monitoring/platform/connect_rum_and_traces/?tab=browserrum
     * Rum & Trace 와 연결을 위해 필요한 옵션
     * */
    allowedTracingUrls: [/https:\/\/.*\.cashnote\.kr/],
    /**
     * 기본적으로는 옵션은 mask로 쓰여야 하지만, 팀 내 요청에 따라서 mask-user-input 옵션 사용
     * 대신 개인정보가 노출되는 부분은 data-dd-privacy="mask" 속성을 무조건 추가해주어야 함.
     * https://koreacreditdata.slack.com/archives/CQG5QADT5/p1693192462918769?thread_ts=1681966998.601409&cid=CQG5QADT5
     * */
    defaultPrivacyLevel: 'mask-user-input',
  });

  kcdDatadogLogs.init({
    service: 'market-buyer-front',
    clientToken: 'pube5059ad1fc1207c4765db5316d942fc6',
    env: ENV,
    version: process.env.NEXT_PUBLIC_APP_VERSION, // 배포시 생성되는 release version을 datadog에 똑같이 전달함.
    forwardErrorsToLogs: false, // 명시적으로 수집한 에러만 로그를 남깁니다.
    telemetrySampleRate: 0,
    beforeSend: (log) => {
      const message = log.message.toLowerCase();
      // hackle 에러, 디버그 console 들을 보내지 않음.
      if (message.includes('hackle') || message.includes('console'))
        return false;

      return true;
    },
  });
};

interface MetaData extends Record<string, unknown> {
  componentName?: string;
}

/**
 * API Error Handler
 * */
export function logError(error: Error | OAIApiError, meta?: MetaData) {
  const metaData = {
    ...meta,
    pageName: isClient() ? window.location.pathname : 'server',
  };

  if (isClient()) {
    if (error instanceof OAIApiError) {
      const obj = {
        ...metaData,
        ...error,
      };
      kcdDatadogLogs.logger.error(
        `Open Api Error: ${error.message}`,
        obj,
        error,
      );
      return;
    }
    kcdDatadogLogs.logger.error(
      `${error.name}: ${error.message}`,
      metaData,
      error,
    );
  }
}

/**
 * API를 제외한 다른 에러들을 수집하기 위한 함수
 * */
export function logMessage(message: string, meta?: MetaData) {
  const href = isClient() ? window.location.href : 'server';
  const metaData = { ...meta, href };
  kcdDatadogLogs.logger.warn(message, metaData);
}
